import { TrackedArray, TrackedObject } from 'tracked-built-ins';

/**
 * Given a plain JS array, return a deeply-tracked TrackedArray. For elements of the array that are themselves arrays or objects,
 * return deeply-tracked versions of those. Elements that are NOT arrays or objects are pushed directly into the TrackedArray.
 */
function deeplyTrackedArray(arr: Array<unknown>): TrackedArray {
  const trackedArray = new TrackedArray();
  for (const item of arr) {
    if (typeof item === 'object') {
      if (Array.isArray(item)) {
        trackedArray.push(deeplyTrackedArray(item));
      } else if (item === null) {
        trackedArray.push(null);
      } else {
        trackedArray.push(deeplyTrackedObject(item));
      }
    } else {
      trackedArray.push(item);
    }
  }

  return trackedArray;
}
/**
 * Given a POJO, create a deeply-tracked version of that object. For values that are arrays, create deeply-tracked TrackedArrays
 * and for values that are objects, create deeply-tracked TrackedObjects. This process happens recursively, so that nested objects
 * and arrays are "converted" to tracked versions.
 * For now, only arrays and objects get the deep-tracking treatment; other structures such as `Map`, `Set`, `WeakMap`, and `WeakSet`,
 * are not currently-handled. If you have a need to support these, feel free to go ahead and do it!
 * This function is meant to handle simple cases; it will undoubtedly be possible to find cases that this function cannot handle.
 * (Circular references are but one example.) The intention here is mostly to be able to create deeply-tracked objects from an
 * object that's received as part of an API response - for use cases beyond that, nothing is guaranteed.
 */
export default function deeplyTrackedObject(obj: object): object {
  const trackedObject = new TrackedObject();
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        trackedObject[key] = deeplyTrackedArray(value);
      } else if (value === null) {
        trackedObject[key] = null;
      } else {
        trackedObject[key] = deeplyTrackedObject(<object>value);
      }
    } else {
      trackedObject[key] = value;
    }
  }

  return trackedObject;
}
